import { useState, useRef, useCallback, useMemo } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useSelectionStore } from '../stores/selectionStore';
import { AppNode, Connection } from '../types';

// 方案 B：自定义拖拽层，完全绕过 React Flow 拖动机制（文档 5.6）
// 在节点 wrapper 的 onMouseDownCapture 捕获阶段拦截，阻止 React Flow 接管拖动，
// 自建浮层跟随鼠标，原节点全程不动，零闪烁。

export interface UseCopyDragParams {
  saveHistory: () => void;
  pasteAt: (sourceNodes: AppNode[], sourceConns: Connection[], delta: { deltaX: number; deltaY: number }) => string[];
  screenToFlowPosition: (pos: { x: number; y: number }) => { x: number; y: number };
}

export interface UseCopyDragReturn {
  // 绑到节点 wrapper 的 onMouseDownCapture
  bindNodeMouseDown: (nodeId: string) => (e: React.MouseEvent) => void;
  // 浮层可见性，传给 CopyDragOverlay
  overlayVisible: boolean;
  // 浮层 DOM ref（CopyDragOverlay 用，零重渲染拖动）
  overlayRef: React.RefObject<HTMLDivElement | null>;
}

// 子元素豁免：点中这些元素时不拦截，放行节点内编辑交互
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  // 带 nodrag class 的元素（React Flow 约定，禁用节点拖动）
  if (target.closest('.nodrag')) return true;
  return false;
}

export function useCopyDrag({
  saveHistory,
  pasteAt,
  screenToFlowPosition,
}: UseCopyDragParams): UseCopyDragReturn {
  const [overlayVisible, setOverlayVisible] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const sourceNodesRef = useRef<AppNode[]>([]);
  const sourceConnsRef = useRef<Connection[]>([]);
  const dragStartFlowRef = useRef<{ x: number; y: number } | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  // 拖拽期间的画布缩放（浮层 transform 需保留 scale，与原节点尺寸一致）
  const zoomRef = useRef(1);

  // 性能优化：screenToFlowPosition 用 ref 缓存，让 bindNodeMouseDown 的 useCallback 依赖稳定
  // （reactFlowInstance.screenToFlowPosition 引用可能随渲染变化，用 ref 解耦）
  const sfPosRef = useRef(screenToFlowPosition);
  sfPosRef.current = screenToFlowPosition;

  const performCleanup = useCallback(() => {
    setOverlayVisible(false);
    // 清空浮层内克隆的 DOM
    if (overlayRef.current) {
      overlayRef.current.innerHTML = '';
    }
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    sourceNodesRef.current = [];
    sourceConnsRef.current = [];
    dragStartFlowRef.current = null;
  }, []);

  // 克隆选中节点的真实 DOM 到浮层容器，保持多选相对布局
  // 视觉与原节点完全一致（含图片/视频/文字等内容），拖动时只移动浮层容器
  // 关键：覆盖克隆节点原始 transform，改为相对第一个节点的偏移定位
  // 浮层容器提供 scale(zoom) 上下文 + translate 跟随鼠标
  const cloneNodesToOverlay = useCallback((nodes: AppNode[]) => {
    const container = overlayRef.current;
    if (!container || nodes.length === 0) return;
    container.innerHTML = '';

    const firstX = nodes[0].x;
    const firstY = nodes[0].y;

    for (const n of nodes) {
      const el = document.querySelector(`.react-flow__node[data-id="${n.id}"]`) as HTMLElement | null;
      if (!el) continue;
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.left = '0';
      clone.style.top = '0';
      clone.style.margin = '0';
      clone.style.pointerEvents = 'none';
      // 覆盖 React Flow 原始 transform（translate(画布坐标)），
      // 改为相对第一个节点的偏移：第一个节点落在浮层原点(0,0)，其余保持相对布局
      // 浮层容器的 scale(zoom) 会自动缩放偏移量，与原画布视觉一致
      clone.style.transform = `translate(${n.x - firstX}px, ${n.y - firstY}px)`;
      clone.classList.remove('selected', 'dragging');
      container.appendChild(clone);
    }

    // 浮层容器的 transform 由 mousemove 统一设置（translate + scale）
    container.style.transformOrigin = '0 0';
  }, []);

  // 依赖 [saveHistory, pasteAt, performCleanup]：三者均为 useCallback 零依赖，引用稳定
  // → bindNodeMouseDown 引用稳定 → NodeWithZoom/customNodeTypes 不会因拖动重建
  const bindNodeMouseDown = useCallback((nodeId: string) => (e: React.MouseEvent) => {
    if (e.button !== 0 || !e.altKey) return;
    if (isInteractiveTarget(e.target)) return;

    e.preventDefault();
    e.stopPropagation();

    const selectedIds = useSelectionStore.getState().getSelectedArray();
    const ids = selectedIds.includes(nodeId) ? selectedIds : [nodeId];
    const nodeMap = useNodeStore.getState().nodeMap;
    const sourceNodes: AppNode[] = [];
    for (const id of ids) {
      const n = nodeMap.get(id);
      if (n) sourceNodes.push(n);
    }
    if (sourceNodes.length === 0) return;

    const selectedSet = new Set(ids);
    const internalConns = useNodeStore.getState().connections.filter(
      c => selectedSet.has(c.from) && selectedSet.has(c.to),
    );

    sourceNodesRef.current = sourceNodes;
    sourceConnsRef.current = internalConns;
    dragStartFlowRef.current = sfPosRef.current({ x: e.clientX, y: e.clientY });

    // 先读取当前画布缩放并存入 ref（cloneNodesToOverlay 和定位都依赖它）
    const viewportEl = document.querySelector('.react-flow__viewport') as HTMLElement | null;
    const viewportTransform = viewportEl ? getComputedStyle(viewportEl).transform : 'none';
    let zoom = 1;
    if (viewportTransform && viewportTransform !== 'none') {
      try {
        const m = new DOMMatrixReadOnly(viewportTransform);
        zoom = m.a;
      } catch {}
    }
    zoomRef.current = zoom;

    // 记录 mousedown 时鼠标相对第一个节点左上角的屏幕偏移
    // 拖动时浮层 translate = 鼠标坐标 - 该偏移，让被拖动节点保持按下时的相对位置跟随鼠标
    const firstEl = document.querySelector(`.react-flow__node[data-id="${sourceNodes[0].id}"]`) as HTMLElement | null;
    const firstRect = firstEl ? firstEl.getBoundingClientRect() : null;
    const grabOffsetX = firstRect ? e.clientX - firstRect.left : 0;
    const grabOffsetY = firstRect ? e.clientY - firstRect.top : 0;
    // 克隆节点已重置为相对第一个节点的偏移定位（第一个节点落在浮层原点 0,0），
    // 浮层容器只需 translate(鼠标 - grabOffset) scale(zoom) 即可让第一个节点跟随鼠标
    const computeTranslate = (clientX: number, clientY: number) => ({
      x: clientX - grabOffsetX,
      y: clientY - grabOffsetY,
    });

    setOverlayVisible(true);

    // 浮层容器初始位置：让被拖动节点保持按下时的位置
    const applyTransform = (clientX: number, clientY: number) => {
      if (overlayRef.current) {
        const t = computeTranslate(clientX, clientY);
        overlayRef.current.style.transform = `translate(${t.x}px, ${t.y}px) scale(${zoomRef.current})`;
      }
    };
    // 跟踪最新鼠标位置：portal 挂载有延迟，tryClone 时用最新位置避免浮层落在按下点
    let latestX = e.clientX;
    let latestY = e.clientY;
    applyTransform(latestX, latestY);
    // 克隆真实节点 DOM 到浮层：setOverlayVisible 触发的 portal 挂载是异步的，
    // 用轮询重试确保 overlayRef.current 存在后执行克隆，避免浮层空白
    const tryClone = (attempt: number) => {
      const el = overlayRef.current;
      if (el) {
        cloneNodesToOverlay(sourceNodes);
        applyTransform(latestX, latestY);
      } else if (attempt < 10) {
        requestAnimationFrame(() => tryClone(attempt + 1));
      }
    };
    requestAnimationFrame(() => tryClone(0));

    const onMove = (ev: MouseEvent) => {
      latestX = ev.clientX;
      latestY = ev.clientY;
      applyTransform(latestX, latestY);
    };

    const onUp = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keydown', onKey);
      cleanupRef.current = null;

      const startFlow = dragStartFlowRef.current;
      if (!startFlow) {
        performCleanup();
        return;
      }
      const releaseFlow = sfPosRef.current({ x: ev.clientX, y: ev.clientY });
      const delta = {
        deltaX: releaseFlow.x - startFlow.x,
        deltaY: releaseFlow.y - startFlow.y,
      };

      saveHistory();
      const newIds = pasteAt(sourceNodesRef.current, sourceConnsRef.current, delta);
      useSelectionStore.getState().setSelectedNodeIds(newIds);

      performCleanup();
    };

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('keydown', onKey);
        cleanupRef.current = null;
        performCleanup();
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keydown', onKey);
    cleanupRef.current = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keydown', onKey);
    };
  }, [saveHistory, pasteAt, performCleanup, cloneNodesToOverlay]);

  // 返回值用 useMemo 稳定引用：平时 overlayVisible=false 稳定，
  // bindNodeMouseDown 引用稳定，overlayRef 稳定 → 返回对象引用稳定
  // → FlowEditor 的 NodeWithZoom/customNodeTypes 不重建，避免 140 节点逐帧重建
  return useMemo(() => ({
    bindNodeMouseDown,
    overlayVisible,
    overlayRef,
  }), [bindNodeMouseDown, overlayVisible, overlayRef]);
}
